import React from 'react';
import PropTypes from 'prop-types';
import { Table, Empty } from 'antd';
import { withTranslation } from 'react-i18next';

import './SummaryTable.less';

import getTranslatedTableHeaders from '../../utils/translationHelpers';
import {
  calculateObjValues,
  getTotalValue,
  getSignedValue,
} from '../../utils/balanceGroupStatsHelpers';
import { sum } from '../../utils/math';
import StatsTitle from './StatsTitle';
import { getDate } from '../../utils/dateTimeHelpers';
import { UNITS_EXTENDED } from '../../constants/units';

// Add the new helper function
const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getDataHeaders = type => ([
  {
    title: type === 'net' ? 'net' : 'amount',
    dataIndex: 'amount',
    key: 'amount',
  }, {
    title: 'interConnectionPoint',
    dataIndex: 'interConnectionPoint',
    key: 'interConnectionPoint',
  },
]);


const remappedData = (data, unit, t, type) => {
  if (!data) return [];

  // Create a flat version of the data (no nested structures)
  // Track which fields have nested structures for expandable table rows to use later
  // Flattening is needed for calculateObjValues() to work
  const flatData = {};
  const expandableData = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // This is a nested structure that should be expandable
      expandableData[key] = value;
      Object.entries(value).forEach(([childKey, childValue]) => {
        if (childValue !== undefined) {
          flatData[`${key}.${childKey}`] = childValue;
        }
      });
    } else {
      flatData[key] = value;
    }
  });

  // Calculate values for all data
  const convertedData = calculateObjValues(flatData, unit);
  const total = getTotalValue(convertedData, unit);
  const amount = { ...convertedData, total };

  const result = [];
  // Process items in original order to maintain data structure
  Object.keys(data).forEach(key => {
    if (expandableData[key]) {
      // Handle expandable rows
      const children = expandableData[key];
      const childKeys = Object.keys(children).map(childKey => `${key}.${childKey}`);

      // Get the child values from the converted data that is in right unit
      const childValues = childKeys.map(childKey => amount[childKey]);
      const totalValue = sum(...childValues);

      result.push({
        key,
        amount: getSignedValue(totalValue, type),
        interConnectionPoint: t(`balanceContainer.label.${key}`),
        children: childKeys.map(childKey => {
          const childName = childKey.split('.').pop();
          return {
            key: childKey,
            amount: getSignedValue(amount[childKey], type),
            interConnectionPoint: t(`balanceContainer.label.${key}${capitalizeFirstLetter(childName)}`),
          };
        }),
      });
    } else if (key !== 'total') {
      // Handle non-expandable rows
      result.push({
        key,
        amount: getSignedValue(amount[key] ?? 0, type),
        interConnectionPoint: t(`balanceContainer.label.${key}`),
      });
    }
  });

  // Add total row at the end if it exists
  if (amount.total) {
    result.push({
      key: 'total',
      amount: getSignedValue(amount.total, type),
      interConnectionPoint: t(type === 'net' ? 'balanceContainer.label.total' : `balanceContainer.label.total${capitalizeFirstLetter(type)}`),
    });
  }

  return result;
};


const renderContent = (type, data, unit, isLoading, t, expandedKeys, setExpandedKeys) => (
  (!isLoading && data === null)
    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    : (
      <Table
        className={`stats-summary-table-${type}`}
        data-testid={`stats-summary-table-${type}`}
        columns={getTranslatedTableHeaders(getDataHeaders(type), t)}
        dataSource={isLoading ? [] : remappedData(data, unit, t, type)}
        pagination={false}
        loading={isLoading}
        expandedRowKeys={expandedKeys}
        onExpand={(expanded, record) => {
          if (expanded) {
            setExpandedKeys([...expandedKeys, record.key]);
          } else {
            setExpandedKeys(expandedKeys.filter(k => k !== record.key));
          }
        }}
        rowKey={(record) => record.key}
      />
    )
);

const SummaryTable = (props) => {
  const {
    data,
    isLoading,
    type,
    t,
    selectedDate,
    unit,
  } = props;

  const [expandedKeys, setExpandedKeys] = React.useState(['acquisitions', 'surrenders']);

  return (
    <>
      <StatsTitle
        title={t(`balanceContainer.title.${type}`)}
        description={t('balanceContainer.label.forGasDay', { selectedDate: getDate(selectedDate) })}
      />

      <div className="stats-summary-table">
        {renderContent(type, data, unit, isLoading, t, expandedKeys, setExpandedKeys)}
      </div>
    </>
  );
};

SummaryTable.displayName = 'SummaryTable';
const acquisitionOrSurrenderType = PropTypes.exact({
  otc: PropTypes.string.isRequired,
  gasExchange: PropTypes.string.isRequired,
});

const entryType = PropTypes.exact({
  biogas: PropTypes.string,
  import: PropTypes.string,
  acquisitions: PropTypes.oneOfType([
    PropTypes.string,
    acquisitionOrSurrenderType,
  ]),
});
const exitType = PropTypes.exact({
  energy: PropTypes.string,
  export: PropTypes.string,
  surrenders: PropTypes.oneOfType([
    PropTypes.string,
    acquisitionOrSurrenderType,
  ]),
});
const netValueType = PropTypes.exact({
  domesticEntryExit: PropTypes.string,
  vtp: PropTypes.string,
  importsExports: PropTypes.string,
});
SummaryTable.propTypes = {
  t: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  type: PropTypes.oneOf(['entry', 'exit', 'net']).isRequired,
  data: PropTypes.oneOfType([entryType, exitType, netValueType]),
  selectedDate: PropTypes.string.isRequired,
  unit: PropTypes.oneOf(Object.values(UNITS_EXTENDED)),
};
SummaryTable.defaultProps = {
  data: null,
  isLoading: false,
  unit: UNITS_EXTENDED.MWH,
};

export default withTranslation()(SummaryTable);
export {
  SummaryTable as PureComponent,
};
